/**
 * useTransactionCardData — single entry point for deriving every value
 * any transaction card needs to render. No card variant reads the raw
 * transaction directly; they all read the object this hook returns.
 *
 * Returns null when the transaction can't be safely rendered:
 *   - transactionId is falsy
 *   - transaction entity isn't in marketplaceData yet
 *   - a relationship (listing / provider / customer / booking / image)
 *     isn't in the entity store yet (race with in-flight query —
 *     denormalisedEntities throws; we catch and return null so the
 *     card simply doesn't render this pass, then re-renders when the
 *     missing entity arrives)
 *   - tx.listing is deleted (isListingDeleted)
 *   - tx's process state can't be resolved (isUnresolvedTx)
 *
 * This replaces the per-page derivation code that RenterDashboardPage,
 * ProviderDashboardPage, BookingsPage, etc. each implemented in
 * slightly different ways. One source of truth — adding a new card
 * surface cannot introduce the class of bugs we kept hitting in the
 * old derivation paths (raw `tx.attributes.booking.start` reads,
 * missing emoji fallbacks, inconsistent null checks).
 */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { denormalisedEntities } from '../../util/data';
import { getCategoryEmoji } from '../../util/listingHelpers';
import {
  getTxState,
  getTxProcessName,
  isListingDeleted,
  isUnresolvedTx,
  getBookingStart,
  getBookingEnd,
  getListingCardImage,
  getServiceLabel,
} from '../../util/transactionCards';

export const useTransactionCardData = transactionId => {
  // Pull the whole entity map. Reference changes whenever any entity is
  // added/replaced (immer produces a new map on each dispatch that
  // mutates marketplaceData.entities). useMemo below re-derives only
  // when entities or transactionId actually change.
  const entities = useSelector(state => state.marketplaceData?.entities);

  return useMemo(() => {
    if (!entities || !transactionId) return null;

    const txEntity = entities.transaction?.[transactionId];
    if (!txEntity) return null;

    let tx;
    try {
      const [resolved] = denormalisedEntities(
        entities,
        [{ id: txEntity.id, type: 'transaction' }],
        /* throwIfNotFound */ false
      );
      if (!resolved) return null;
      tx = resolved;
    } catch (e) {
      // Denormalisation threw — a relationship (listing, provider,
      // booking, image) isn't in the store yet. Quiet null-return;
      // useMemo re-fires when entities updates on the next dispatch.
      return null;
    }

    if (isListingDeleted(tx)) return null;
    if (isUnresolvedTx(tx)) return null;

    const { firstImage, variants: imageVariants } = getListingCardImage(tx.listing);
    const startIso = getBookingStart(tx);
    const endIso = getBookingEnd(tx);

    // Quote (negotiation) transactions have no booking object — fall back
    // to the renter's stated event date (protectedData, written at
    // request-quote time) so cards still show when the party is. Anchored
    // to local midnight so a date-only string doesn't shift a day west of
    // UTC. Layouts collapse same-day ranges to a single date.
    const eventDateIso = !startIso ? tx.attributes?.protectedData?.eventDate : null;
    const eventDate = eventDateIso ? new Date(`${eventDateIso}T00:00:00`) : null;

    return {
      tx,
      state: getTxState(tx),
      // Canonical process name — lets layouts pass it to StatusBadge so
      // states that collide across processes (e.g. 'delivered') get the
      // right label. See getTxProcessName in util/transactionCards.
      processName: getTxProcessName(tx),
      listing: tx.listing,
      provider: tx.provider,
      customer: tx.customer,
      booking: tx.booking,
      startDate: startIso ? new Date(startIso) : eventDate,
      endDate: endIso ? new Date(endIso) : eventDate,
      firstImage,
      imageVariants,
      listingTitle: tx.listing?.attributes?.title || '—',
      providerName: tx.provider?.attributes?.profile?.displayName || '—',
      customerName: tx.customer?.attributes?.profile?.displayName || '—',
      emoji: getCategoryEmoji(tx.listing?.attributes?.publicData?.categoryLevel1),
      // payinTotal is the renter's view of price; payoutTotal is the host's.
      // Expose both — layouts pick whichever matches their variant.
      payinCents: tx.attributes?.payinTotal?.amount,
      payoutCents: tx.attributes?.payoutTotal?.amount,
      serviceLabel: getServiceLabel(tx),
    };
  }, [entities, transactionId]);
};
